import { useMutation } from '@apollo/client'
import toast from 'react-hot-toast'
import styled from 'styled-components'

import {
  GET_PRODUCTION_EXPORTS
} from 'data/layoutcreator/queries/production'
import { DELETE_PRODUCTION_EXPORT } from 'data/layoutcreator/mutations/production'
import {
  ProductionExportStatuses,
  ProductionExportType
} from 'shared/types/layoutcreator/graphql'

const Table = styled.table`
  width: 100%;
`

const Th = styled.th`
  color: ${({ theme }) => theme.color.base.c7};
  font-weight: 600;
  text-transform: uppercase;
  font-size: 12px;
  text-align: left;
  padding-bottom: 12px;
`

const Tr = styled.tr`
  cursor: default;
`

const Td = styled.td`
  padding: 20px 0;
  font-size: 14px;
  border-bottom: ${({ theme }) => `1px solid ${theme.color.base.c3}`};
`

const Empty = styled.div`
  padding: 16px 0;
  color: ${({ theme }) => theme.color.base.c7};
  font-size: 13px;
`

const Delete = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.color.error};
  cursor: pointer;
  padding: 0;

  &:hover {
    text-decoration: underline;
  }

  &:disabled {
    opacity: 0.5;
    cursor: default;
    text-decoration: none;
  }
`

const Status = styled.span<{ value: ProductionExportStatuses }>`
  text-transform: capitalize;
  color: ${({ value, theme }) =>
    value === ProductionExportStatuses.Failed
      ? theme.color.error
      : value === ProductionExportStatuses.Completed
      ? theme.color.brand_02
      : theme.color.base.c2};
`

const TypePill = styled.span<{ value: ProductionExportType }>`
  display: inline-block;
  padding: 0 10px;
  border-radius: 9999px;
  background: ${({ theme }) => theme.color.base.c3};
  color: ${({ theme }) => theme.color.base.c2};
  font-size: 13px;
  line-height: 22px;
  text-transform: capitalize;
`

function formatDate(value?: Date | string | null): string {
  if (!value) {
    return ''
  }
  const date = new Date(value)
  const day = String(date.getDate()).padStart(2, '0')
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const year = date.getFullYear()
  const hours = String(date.getHours()).padStart(2, '0')
  const minutes = String(date.getMinutes()).padStart(2, '0')
  return `${day}.${month}.${year} - ${hours}:${minutes}`
}

type ProductionExportRow = {
  id: string
  type: ProductionExportType
  pages: number
  startedAt: Date | string | null
  authorFullName: string
  status: ProductionExportStatuses
}

type ProductionExportTableProps = {
  groupId: string
  projectId: string
  items: ProductionExportRow[]
}

const ProductionExportTable: React.FC<ProductionExportTableProps> = ({
  groupId,
  projectId,
  items
}) => {
  const [deleteProductionExport, { loading: isDeleting }] = useMutation(
    DELETE_PRODUCTION_EXPORT,
    {
      refetchQueries: [
        {
          query: GET_PRODUCTION_EXPORTS,
          variables: { groupId, projectId }
        }
      ],
      awaitRefetchQueries: true
    }
  )

  const handleDelete = async (id: string) => {
    // Confirm before deleting — these records are typically used to
    // unblock stuck users, but they're still real production export
    // history items.
    const ok = window.confirm(
      'Delete this production export? This will also remove the linked project export so the user can start a new attempt.'
    )
    if (!ok) {
      return
    }

    await toast.promise(
      deleteProductionExport({ variables: { id } }),
      {
        loading: 'Deleting production export...',
        success: 'Production export deleted',
        error: 'Failed to delete production export'
      }
    )
  }

  if (!items.length) {
    return <Empty>No production exports for this project.</Empty>
  }

  return (
    <Table>
      <thead>
        <tr>
          <Th>Type</Th>
          <Th>Pages count</Th>
          <Th>Export date</Th>
          <Th>Export by</Th>
          <Th>Status</Th>
          <Th></Th>
        </tr>
      </thead>
      <tbody>
        {items.map(item => (
          <Tr key={item.id}>
            <Td>
              <TypePill value={item.type}>{item.type}</TypePill>
            </Td>
            <Td>{item.pages}</Td>
            <Td>{formatDate(item.startedAt)}</Td>
            <Td>{item.authorFullName}</Td>
            <Td>
              <Status value={item.status}>{item.status}</Status>
            </Td>
            <Td>
              <Delete
                onClick={() => handleDelete(item.id)}
                disabled={isDeleting}
              >
                Delete
              </Delete>
            </Td>
          </Tr>
        ))}
      </tbody>
    </Table>
  )
}

export default ProductionExportTable
